import { apiClient, bearer, unwrap } from './client';
import type { JournalEntryOut, TransactionJournalEntryOut } from './types';

/** Fetch the header + child GL lines for one transaction. Returns 404
 *  when the transaction id doesn't exist.
 *
 *  Practitioner-only on the server side; cross-account allowed. */
export function getTransactionJournalEntry(
  transactionId: string,
  idToken: string,
): Promise<TransactionJournalEntryOut> {
  return apiClient
    .GET('/api/transactions/{transaction_id}/journal-entry', {
      params: { path: { transaction_id: transactionId } },
      headers: bearer(idToken),
    })
    .then(unwrap<TransactionJournalEntryOut>);
}

/** Unified journal-entry fetcher used by the finding-detail UI.
 *
 *  Reads `/api/transactions/{id}/journal-entry` via the finding's
 *  `transaction_id_uuid`. Returns `null` when the finding carries no
 *  UUID (historical findings that predate the transactions table and
 *  were never backfilled) — the panel renders its empty state.
 *
 *  Adapts the response to the legacy `JournalEntryOut` shape so the
 *  rendering table needs no changes. The `JournalEntryTable` reads
 *  source columns out of `lines[*]`; the legacy shape stored the raw
 *  line dict at the top level, the new shape nests it under `raw`, so
 *  we unpack `raw` to maintain shape compatibility for NetSuite-source
 *  rows. For per-bill sources (QBO) the route returns a synthesized
 *  1-line response with an empty `raw` — the table renders empty cells. */
export async function getJournalEntryUnified(
  {
    runId,
    transactionIdUuid,
  }: {
    runId: string;
    transactionIdUuid: string | null;
  },
  idToken: string,
): Promise<JournalEntryOut | null> {
  if (!transactionIdUuid) return null;
  const v2 = await getTransactionJournalEntry(transactionIdUuid, idToken);
  return adaptToLegacyShape(v2, runId);
}

function adaptToLegacyShape(
  v2: TransactionJournalEntryOut,
  runId: string,
): JournalEntryOut {
  return {
    audit_run_id: runId,
    transaction_id: v2.external_transaction_id,
    bill_date: v2.bill_date,
    supplier_name: v2.supplier_name ?? '',
    gl_account_name: v2.lines[0]?.gl_account_name ?? '',
    bill: v2.bill,
    tax: v2.tax,
    // Unpack `raw` so JournalEntryTable's column-derivation
    // (`Object.keys(lines[0])`) reads the original source columns.
    // For per-bill sources where raw is empty/sparse, the table will
    // render whatever keys are present — fine.
    lines: v2.lines.map((line) => line.raw),
  };
}
